import React, { useCallback, useEffect, useMemo, useState } from "react";
import "./customerServiceDashboard.css";
import CardPopup from "../../components/cardPopup/cardPopup";
import AcceptPopup from "../../components/acceptPopup/acceptPopup";
import RejectPopup from "../../components/rejectPopup/rejectPopup";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  clearCreditCardDetails,
  fetchCCDetails,
  fetchOrderDetailsAsync,
  resendEmail,
} from "../../redux/reducers/customerAgentDetailsDashboard";
import { useLocation, useParams } from "react-router-dom";
import { Box, createTheme, ThemeProvider, Tooltip } from "@mui/material";
import Toast from "../../components/toast/toast";
import ApiLoader from "../../components/apiLoader/apiLoader";
import { ReactComponent as BackBtn } from "../../assets/icons/arrow-down.svg";
import { ReactComponent as InfoBtn } from "../../assets/icons/info-icon.svg";
import WarningPopup from "../../components/warningPopup/warningPopup";
import OrderLogPopup from "../../components/OrderLogPopup/OrderLogPopup";
import { fetchOrderLogs } from "../../redux/reducers/orderLog";
import {
  getCustomerDashboardSections,
  getCustomerDetails,
  getOrderDetails,
  getPlanDetails,
} from "../../utils/constants";
import CredentialsPopup from "../../components/credentialsPopup/credentialsPopup";
import {
  fetchCustomerCredentials,
  sendCustomerCredentials,
} from "../../redux/reducers/credentialsSlice";

const theme = createTheme({
  components: {
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          width: "179px",
          height: "38.35px",
          backgroundColor: "#fff",
          color: "#252525CC",
          alignContent: "center",
          paddingLeft: "12px",
          boxShadow: "0px 2px 5px 0px #00000026",
          fontSize: "11px",
          fontWeight: 600,
          borderRadius: "6px",
        },
      },
    },
  },
});

const PlanItem = React.memo(({ details }) => (
  <div className="optimun-plan">
    {details.map((detail, index) => (
      <div key={index} className="optimun-plan-item">
        <span className="label">{detail.label}</span>
        <span className="value">{detail.value}</span>
      </div>
    ))}
  </div>
));

const CustomerInfoItem = React.memo(({ label, value }) => (
  <div className="customer-info-item">
    <span className="label">{label}</span>
    <pre className="value">{value}</pre>
  </div>
));

export default function CustomerServiceDashboard() {
  const [isAcceptPopupVisible, setAcceptPopupVisible] = useState(false);
  const [isRejectPopupVisible, setRejectPopupVisible] = useState(false);
  const [isOrderLogPopupVisible, setIsOrderLogPopupVisible] = useState(false);
  const [isCredentialPopupVisible, setIsCredentialPopupVisible] =
    useState(false);
  const navigate = useNavigate();
  const [warningAction, setWarningAction] = useState();
  const [isWarningPopupVisible, setWarningPopupVisible] = useState(false);
  const [toast, setToast] = useState(false);
  const [credentialsToast, setCredentialsToast] = useState(false);
  const [viewLimit, setViewLimit] = useState(false);
  const [limitCount, setLimitCount] = useState(null);
  const [resend, setResend] = useState(null);

  const { creditCardDetails, limit, emailSend } = useSelector(
    (state) => state.customerAgentDetails
  );
  const loadingCreditCard = useSelector(
    (state) => state.customerAgentDetails.isLoading
  );
  const isLoading = useSelector((state) => state?.orderStatus?.isLoading);
  const dispatch = useDispatch();
  const customerDashboardData = useSelector(
    (state) => state?.customerAgentDetails?.orderDetails
  );
  const orderLogData = useSelector((state) => state?.orderLog?.logs);
  const isOrderLogLoading = useSelector((state) => state?.orderLog?.loading);
  const rejectStatus = useSelector((state) => state?.orderStatus?.rejectStatus);
  const credentialsData = useSelector(
    (state) => state?.credentialsData?.data || null
  );
  const isCredentialsloading = useSelector(
    (state) => state?.credentialsData?.loading || false
  );
  const isError = useSelector((state) => state?.credentialsData?.error);
  const isRejectStatusLoading = useSelector(
    (state) => state?.orderStatus?.isStatusesLoading
  );
  const { id } = useParams();
  const orderId = id;
  const [status, setStatus] = useState("");
  const count = customerDashboardData?.retrieval_count;
  const toPascalCase = (word) => {
    return word?.charAt(0)?.toUpperCase() + word?.slice(1)?.toLowerCase();
  };

  const handleOpenCreditCardPopup = useCallback(() => {
    dispatch(fetchCCDetails(orderId));
  }, [dispatch, customerDashboardData]);

  const handleCloseCreditCardPopup = () => {
    setViewLimit(false);
    setResend(false);
    limitCount === 0 && setStatus(creditCardDetails.status);
    dispatch(clearCreditCardDetails());
  };

  const handleOpenAcceptPopup = () => {
    if (status.toLowerCase() === "cancelled") {
      setWarningPopupVisible(true);
      setWarningAction("accept");
    } else {
      setAcceptPopupVisible(true);
    }
  };
  const handleCloseAcceptPopup = () => setAcceptPopupVisible(false);

  const handleOpenRejectPopup = () => {
    if (status.toLowerCase() === "cancelled") {
      setWarningPopupVisible(true);
      setWarningAction("reject");
    } else {
      setRejectPopupVisible(true);
    }
  };

  const handleCloseRejectPopup = () => setRejectPopupVisible(false);

  const handleCloseWarningPopup = () => {
    setWarningPopupVisible(false);
    setIsCredentialPopupVisible(true);
  };
  const handleWarningPopupAction = () => {
    if (warningAction === "accept") {
      setAcceptPopupVisible(true);
      setWarningPopupVisible(false);
    } else if (warningAction === "reject") {
      setWarningPopupVisible(false);
      setRejectPopupVisible(true);
    } else if (warningAction === "credentials") {
      const credentials = {
        pin: credentialsData?.pin,
        password: credentialsData?.password,
      };
      dispatch(sendCustomerCredentials({ orderId, credentials })).then(
        (res) => {
          setCredentialsToast({ header: res?.payload?.message });
        }
      );
      setWarningPopupVisible(false);
    }
  };
  const handleOpenOrderLogPopup = () => {
    dispatch(fetchOrderLogs(orderId));
    setIsOrderLogPopupVisible(true);
  };
  const handleCloseOrderLogPopup = () => setIsOrderLogPopupVisible(false);

  const handleViewCredentialsPopup = () => {
    setIsCredentialPopupVisible(true);
    setWarningAction("credentials");
    dispatch(fetchCustomerCredentials(orderId));
  };

  useEffect(() => {
    emailSend?.status === "REINITIATED" &&
      (setToast({ header: "Email has been sent successfully." }),
      setStatus(emailSend?.status));
    emailSend?.status === "REINITIATED" &&
      setTimeout(() => {
        setToast(null);
      }, 3000);
  }, [emailSend]);

  useEffect(() => {
    dispatch(clearCreditCardDetails());
    dispatch(fetchOrderDetailsAsync(orderId));
    // dispatch(fetchRejectStatusesAsync("REJECTED"));
  }, [dispatch]);

  useEffect(() => {
    setStatus(customerDashboardData?.status);
  }, [customerDashboardData]);

  useEffect(() => {
    const tempLimit = limit
      ? limit?.max_retrieval_count - limit?.retrieval_count
      : customerDashboardData?.max_retrieval_count -
        customerDashboardData?.retrieval_count;
    setLimitCount(tempLimit);
  }, [limit, count]);

  useEffect(() => {
    creditCardDetails && setViewLimit(true);
  }, [creditCardDetails]);

  const planDetails = useMemo(() => {
    return getPlanDetails(customerDashboardData);
  }, [customerDashboardData]);

  const customerDetails = useMemo(
    () => getCustomerDetails(customerDashboardData),
    [customerDashboardData]
  );
  const orderDetails = useMemo(
    () => getOrderDetails(customerDashboardData),
    [customerDashboardData]
  );

  const customerDashboardSections = useMemo(
    () =>
      getCustomerDashboardSections(planDetails, customerDetails, orderDetails),
    [planDetails, customerDetails, orderDetails]
  );

  const handleResend = useCallback(() => {
    dispatch(
      resendEmail({
        orderId: orderId,
      })
    ).then((res) => {
      if (res?.payload) {
        setToast({ header: res?.payload?.message });
        dispatch(fetchOrderDetailsAsync(orderId));
        setRejectPopupVisible(false);
      }
    });
  }, [dispatch, customerDashboardData, orderId]);

  const StatusChip = useCallback(
    ({ status }) => {
      let chipClass = "";
      let chipColor = "";
      let displayText = "";

      switch (status?.toLowerCase()) {
        case "completed":
          chipClass = "complete";
          chipColor = "#2DB683";
          displayText = "Completed";
          break;

        case "rejected_ineligible":
          chipClass = "rejected";
          chipColor = "#FF6347";
          displayText = "Rejected Ineligible";
          break;

        case "rejected_invalid":
          chipClass = "rejected";
          chipColor = "#FF6347";
          displayText = "Rejected Invalid";
          break;
        case "rejected_ineligible_porting":
          chipClass = "rejected_ineligible";
          chipColor = "#FF6347";
          displayText = "Rejected Ineligible for Porting";
          break;
        case "rejected_device_not_supported":
          chipClass = "rejected_ineligible";
          chipColor = "#FF6347";
          displayText = "Rejected Device Not Supported";
          break;
        case "rejected_other":
          chipClass = "rejected";
          chipColor = "#FF6347";
          displayText = "Rejected Other";
          break;
        case "reinitiated":
          chipClass = "reinitiated";
          chipColor = "#A18ADC";
          displayText = "Reinitiated";
          break;

        case "open":
          chipClass = "open";
          chipColor = "#28A5DD";
          displayText = "Open";
          break;

        case "pending":
          chipClass = "pending";
          chipColor = "#FDAB3D";
          displayText = "Pending";
          break;

        case "card_removed":
          chipClass = "card_removed";
          chipColor = "#909090";
          displayText = "Card Removed";
          break;

        default:
          chipClass = "";
          chipColor = "";
          displayText = toPascalCase(status?.toLowerCase() || "");
      }

      return (
        <span
          className={`status-chip-details ${chipClass}`}
          style={{ backgroundColor: chipColor }}
        >
          {displayText}
        </span>
      );
    },
    [status]
  );

  const filterData = JSON.parse(sessionStorage.getItem("filterData"));

  const handleBack = useCallback(() => {
    const params = new URLSearchParams();
    if (filterData?.statusId) params.append("status", filterData.statusId);
    if (filterData?.startDate) params.append("fromDate", filterData.startDate);
    if (filterData?.endDate) params.append("toDate", filterData.endDate);

    navigate(`/customer-status?${params.toString()}`);
    sessionStorage.clear();
  }, [filterData, navigate]);

  const transformLogs = (inputArray) => {
    const currentDate = new Date();
    const today = currentDate.toISOString().split("T")[0]; // Current date in "YYYY-MM-DD"
    const yesterday = new Date(currentDate);
    yesterday.setDate(currentDate.getDate() - 1);
    const yesterdayDate = yesterday.toISOString().split("T")[0]; // Yesterday's date in "YYYY-MM-DD"

    return inputArray.map((item) => {
      const createdDate = new Date(item.created_at);
      const createdDateString = createdDate.toISOString().split("T")[0]; // Extract "YYYY-MM-DD"
      let prefix = "";
      if (createdDateString === today) {
        prefix = "Today ";
      } else if (createdDateString === yesterdayDate) {
        prefix = "Yesterday ";
      }

      const title = item.event || "Unknown Event";
      const timestamp =
        prefix +
        createdDate.toLocaleString("en-US", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
          hour12: false,
        });
      const user = item.user_info?.name || "Unknown User";
      return { title, timestamp, user };
    });
  };

  const logs = transformLogs(orderLogData);

  useEffect(() => {
    setTimeout(() => {
      setToast(false);
      setCredentialsToast(false);
    }, 3500);
  }, [toast, credentialsToast]);

  return (
    <>
      <ApiLoader
        loading={
          loadingCreditCard ||
          isLoading ||
          isOrderLogLoading ||
          isCredentialsloading ||
          isRejectStatusLoading
        }
      />
      <div className="optimum-plan-page">
        <div className="previous-page" onClick={handleBack}>
          <BackBtn className="back-btn" />
          <p>Back</p>
        </div>
        {/* Configure Optimum Plan Section */}

        <header className="section-header">
          <Box className="header-panel">
            <h2>{customerDashboardSections[0].name}</h2>
            <StatusChip status={status} />
            {customerDashboardData?.reject_reason &&
              status === "REJECTED_OTHER" && (
                <div className="tooltipContainer">
                  <Tooltip
                    title={
                      <span className="rejected-tooltip">
                        {customerDashboardData?.reject_reason}
                      </span>
                    }
                    arrow
                  >
                    <InfoBtn />
                  </Tooltip>
                </div>
              )}
          </Box>
          <div className="buttons">
            <button className="log-btn" onClick={handleOpenOrderLogPopup}>
              View Log Details
            </button>
            {isOrderLogPopupVisible && (
              <OrderLogPopup logs={logs} onClose={handleCloseOrderLogPopup} />
            )}
          </div>
        </header>
        {toast && <Toast toast={toast} />}
        <section className="configure-plan">
          <div className="plan-details">
            <PlanItem details={customerDashboardSections[0].fieldSet} />
          </div>
          {status?.toLowerCase() === "pending" ||
          status?.toLowerCase() === "open" ||
          status?.toLowerCase() === "card_removed" ||
          status?.toLowerCase() === "reinitiated" ? (
            <div className="bottom-section">
              <button className="button reject" onClick={handleOpenRejectPopup}>
                Reject
              </button>

              {status?.toLowerCase() === "open" ||
              status?.toLowerCase() === "reinitiated" ? null : (
                <button
                  className="button accept"
                  onClick={handleOpenAcceptPopup}
                >
                  Complete
                </button>
              )}
              {isWarningPopupVisible && (
                <WarningPopup
                  onClose={handleCloseWarningPopup}
                  onConfirm={handleWarningPopupAction}
                  warningAction={warningAction}
                />
              )}
              {isRejectPopupVisible && (
                <RejectPopup
                  onClose={handleCloseRejectPopup}
                  status={rejectStatus}
                  setToast={setToast}
                />
              )}
              {isAcceptPopupVisible && (
                <AcceptPopup
                  onClose={handleCloseAcceptPopup}
                  setToast={setToast}
                />
              )}
              {/* {toast && !isError && <Toast toast={credentialsToast} />} */}
            </div>
          ) : null}
        </section>

        {/* Customer Info Section */}
        <div className="customer-info-panel">
          <h2 className="customer-info-header">
            {customerDashboardSections[1].name}
          </h2>
          <div className="customer-info-buttons">
            {credentialsToast && !isError && <Toast toast={credentialsToast} />}
            {customerDashboardData && (
              <button className="log-btn" onClick={handleViewCredentialsPopup}>
                Send PIN/Pwd
              </button>
            )}
            {isWarningPopupVisible && (
              <WarningPopup
                onClose={handleCloseWarningPopup}
                onConfirm={handleWarningPopupAction}
                warningAction={warningAction}
              />
            )}
            {customerDashboardData &&
              customerDashboardData?.status?.toLowerCase() !== "completed" &&
              status?.toLowerCase() !== "rejected_ineligible" && (
                <button
                  onClick={() => {
                    setResend(true);
                    setViewLimit(true);
                    setLimitCount(null);
                  }}
                  className="resend-btn"
                >
                  Resend
                </button>
              )}
            {customerDashboardData &&
              customerDashboardData?.status?.toLowerCase() === "pending" && (
                <button
                  className={`view-cc ${limitCount === 0 && "btn-diabled"}`}
                  onClick={() => {
                    setViewLimit(true);
                  }}
                  disabled={limitCount === 0 ? true : false}
                >
                  View Credit Card
                </button>
              )}
            {viewLimit && (
              <CardPopup
                onClose={handleCloseCreditCardPopup}
                cardDetails={creditCardDetails}
                setViewLimit={setViewLimit}
                limitCount={limitCount}
                handleOpenCreditCardPopup={handleOpenCreditCardPopup}
                resend={resend}
                handleResend={handleResend}
              />
            )}
            {isCredentialPopupVisible && (
              <CredentialsPopup
                onClose={() => setIsCredentialPopupVisible(false)}
                credentials={credentialsData}
                setWarningPopupVisible={setWarningPopupVisible}
              />
            )}
          </div>
        </div>

        <section className="customer-info">
          <div className="customer-details-grid">
            {customerDashboardSections[1].fieldSet.map((info, index) => (
              <CustomerInfoItem
                key={index}
                label={info.label}
                value={info.value}
              />
            ))}
          </div>
        </section>

        {/* Order Info Section */}
        <h2 className="customer-info-header">
          {customerDashboardSections[2].name}
        </h2>
        <section className="customer-info">
          <div className="customer-details-grid">
            {customerDashboardSections[2].fieldSet.map((info, index) => (
              <CustomerInfoItem
                key={index}
                label={info.label}
                value={info.value}
              />
            ))}
          </div>
        </section>
      </div>
    </>
  );
}
